import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type ClinicWhatsAppWebhookSubscriptionRow = {
  clinic_id: string;
  waba_id: string | null;
  callback_url: string | null;
  verify_token_name: string;
  app_secret_name: string;
  is_active: boolean;
  updated_at: string | null;
};

export type ClinicWhatsAppWebhookSubscriptionInput = {
  wabaId?: string | null;
  callbackUrl?: string | null;
  verifyTokenName: string;
  appSecretName: string;
  isActive: boolean;
};

export function useClinicWhatsAppWebhookSubscription(clinicId: string | null) {
  return useQuery({
    queryKey: ["clinic-whatsapp-webhook-subscription", clinicId],
    queryFn: async (): Promise<ClinicWhatsAppWebhookSubscriptionRow | null> => {
      if (!clinicId) return null;

      const { data, error } = await supabase
        .from("clinic_whatsapp_webhook_subscriptions")
        .select("*")
        .eq("clinic_id", clinicId)
        .maybeSingle();

      if (error) throw error;
      return (data ?? null) as unknown as ClinicWhatsAppWebhookSubscriptionRow | null;
    },
    enabled: !!clinicId,
    staleTime: 1000 * 30,
  });
}

export function useUpsertClinicWhatsAppWebhookSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { clinicId: string; subscription: ClinicWhatsAppWebhookSubscriptionInput }) => {
      const { clinicId, subscription } = input;

      const payload = {
        clinic_id: clinicId,
        waba_id: subscription.wabaId ?? null,
        callback_url: subscription.callbackUrl ?? null,
        verify_token_name: subscription.verifyTokenName,
        app_secret_name: subscription.appSecretName,
        is_active: subscription.isActive,
      };

      const { error } = await supabase
        .from("clinic_whatsapp_webhook_subscriptions")
        .upsert(payload, { onConflict: "clinic_id" });

      if (error) throw error;
      return true;
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["clinic-whatsapp-webhook-subscription", variables.clinicId],
      });
    },
  });
}

